import type { components } from '@bookingdei/api-client/types';
import type { MessageKey } from '@bookingdei/i18n/message-key';

type Category = components['schemas']['Category'];

// The design's per-category copy (its CAT table: noun, unit, booking-rail labels,
// "Is this your hotel?", "Book a slot", …) keyed by the LISTING KIND the category's
// booking template implies — hotel, restaurant, turf — with a generic set for every
// other category (FR-C31: category copy is i18n keyed by category, generic fallback).
// Category titles, specialties, amenities and filter sets come from the registry.
export type LrKind = 'hotel' | 'restaurant' | 'turf' | 'generic';

export function lrKindOf(category: Pick<Category, 'template_id' | 'id'> | undefined): LrKind {
  const id = category?.template_id ?? category?.id;
  return id === 'hotel' || id === 'restaurant' || id === 'turf' ? id : 'generic';
}

export const TURF_CATEGORY_ID = 'turf';

type CopyKey =
  | 'noun'
  // Bangla needs the noun's locative (হোটেলে) and genitive (হোটেলের); English repeats the noun.
  | 'noun_in'
  | 'noun_of'
  | 'plural'
  | 'unit'
  | 'per'
  | 'price_lbl'
  | 'contact'
  | 'showc'
  | 'own_q'
  | 'own_add'
  | 'nearby'
  | 'd1'
  | 'd2'
  | 'pay'
  | 'notify_fine'
  | 'book_btn'
  | 'blurb'
  | 'search_ph'
  | 'price_sub'
  | 'spec_sub'
  | 'book_open'
  | 'price_missing';

const KINDS: LrKind[] = ['hotel', 'restaurant', 'turf', 'generic'];

/** `lr.web.cat.<kind>.<copy>` — every key exists in both dictionaries. */
export function catKey(kind: LrKind, copy: CopyKey): MessageKey {
  return `lr.web.cat.${kind}.${copy}` as MessageKey;
}

export function isLrKind(s: string): s is LrKind {
  return (KINDS as string[]).includes(s);
}

// The design's icon per listing kind (its `i-bed / i-fork / i-ball` choices).
export function kindIcon(kind: LrKind): 'i-bed' | 'i-fork' | 'i-ball' | 'i-store' {
  return kind === 'hotel' ? 'i-bed' : kind === 'restaurant' ? 'i-fork' : kind === 'turf' ? 'i-ball' : 'i-store';
}

// Specialty icons by subcategory key (the design's SPEC_ICON, keyed on the registry
// subcategory key rather than its label).
const SPEC_ICON: Record<string, string> = {
  city_hotel: 'i-metro',
  hotel_rooms: 'i-metro',
  beach_resort: 'i-wave',
  garden_resort: 'i-tree',
  resort: 'i-tree',
  guest_house: 'i-home',
  buffet: 'i-tray',
  bangladeshi: 'i-fork',
  mughlai_kebab: 'i-fork',
  chinese_thai: 'i-fork',
  cafe_lounge: 'i-cup',
  cafe: 'i-cup',
  seafood: 'i-wave',
  fast_food: 'i-burger',
  fine_dining: 'i-wine',
  catering: 'i-tray',
  futsal: 'i-ball',
  seven_a_side: 'i-ball',
  cricket_nets: 'i-ball',
  badminton: 'i-ball',
  basketball: 'i-ball',
  multi_sport: 'i-grid',
};

export function specialtyIcon(key: string | undefined): string {
  return (key && SPEC_ICON[key]) || 'i-spark';
}

/** `{noun}`, `{noun_in}`, `{noun_of}` for copy that names the listing kind. */
export function nounVars(t: (key: MessageKey) => string, kind: LrKind): { noun: string; noun_in: string; noun_of: string } {
  return { noun: t(catKey(kind, 'noun')), noun_in: t(catKey(kind, 'noun_in')), noun_of: t(catKey(kind, 'noun_of')) };
}
